import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarDays, Clock, Loader2, CheckCircle2, Video, Users, Building2, ArrowRight, Check, PlayCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field = 'w-full px-4 py-3 border border-[#E5E7EB] rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#00A79D]/40';

const SignupForm = ({ slug, onDemand }) => {
  const [f, setF] = useState({ name: '', organisation: '', role: '', email: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.organisation.trim() || !f.role.trim() || !f.email.trim()) {
      toast.error('Please complete all fields'); return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/webinars/${slug}/register`, {
        email: f.email.trim(), name: f.name.trim(), organisation: f.organisation.trim(), role: f.role.trim(),
      });
      try { const sid = localStorage.getItem('ctc_sid'); if (sid) axios.post(`${API}/public/track/conversion`, { sid, type: 'webinar' }).catch(() => {}); } catch (e) { /* ignore */ }
      if (data.watch_url) { window.location.href = data.watch_url; return; }
      setDone(true); toast.success(data.message || "You're registered!");
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally { setBusy(false); }
  };

  if (done) return (
    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 text-center" data-testid={`webinar-success-${slug}`}>
      <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
      <p className="font-semibold text-gray-900">You're registered.</p>
      <p className="text-sm text-gray-600 mt-1">We'll email your calendar invite and joining link.</p>
    </div>
  );

  return (
    <form onSubmit={submit} className="space-y-3" data-testid={`webinar-form-${slug}`}>
      <input type="text" required placeholder="Your name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={field} data-testid={`webinar-name-${slug}`} />
      <input type="text" required placeholder="Organisation" value={f.organisation} onChange={(e) => setF({ ...f, organisation: e.target.value })} className={field} data-testid={`webinar-org-${slug}`} />
      <input type="text" required placeholder="Your role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className={field} data-testid={`webinar-role-${slug}`} />
      <input type="email" required placeholder="Work email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={field} data-testid={`webinar-email-${slug}`} />
      <button type="submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-6 py-3 rounded-md transition-colors disabled:opacity-60" data-testid={`webinar-submit-${slug}`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : onDemand ? <PlayCircle className="w-4 h-4" /> : <CalendarDays className="w-4 h-4" />} {onDemand ? 'Watch the recording' : 'Save my seat'}
      </button>
      {onDemand && <p className="text-xs text-gray-400 text-center">Free. We'll email you the recording and slides too.</p>}
    </form>
  );
};

const ICONS = { suppliers: Building2, providers: Users };

const Webinars = () => {
  const [webinars, setWebinars] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/public/webinars`)
      .then((r) => setWebinars(r.data.webinars || []))
      .catch(() => setWebinars([]))
      .finally(() => setLoading(false));
    document.title = 'Live webinars | Care Tech Compare';
  }, []);

  return (
    <div data-testid="page-webinars">
      {/* Hero */}
      <section className="bg-[#1A3A5F]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#2fd0c6] bg-white/10 px-3 py-1 rounded-full mb-5"><Video className="w-3.5 h-3.5" /> On-demand webinar</span>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight font-extrabold text-white leading-tight">See the platform in action</h1>
          <p className="text-lg text-gray-300 mt-5 max-w-2xl mx-auto">
            Watch our supplier webinar on demand - a walkthrough of how care providers compare technology on
            Care Tech Compare, and how to build a listing that gets you shortlisted. Sign up and watch instantly.
          </p>
        </div>
      </section>

      {/* Webinar list */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#00A79D]" /></div>
        ) : webinars.length === 0 ? (
          <p className="text-center text-gray-500 py-16" data-testid="webinars-empty">No webinars scheduled right now - check back soon.</p>
        ) : (
          <div className="grid lg:grid-cols-2 gap-6">
            {webinars.map((w) => {
              const Icon = ICONS[w.slug] || Video;
              return (
                <div key={w.slug} className="border border-[#E5E7EB] rounded-xl bg-white overflow-hidden flex flex-col" data-testid={`webinar-card-${w.slug}`}>
                  <div className="bg-[#F9FAFB] border-b border-[#E5E7EB] p-6">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#00857D] bg-[#00A79D]/10 px-3 py-1 rounded-full"><Icon className="w-3.5 h-3.5" /> For {w.audience.toLowerCase()}</span>
                      {w.on_demand && <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1A3A5F] bg-[#1A3A5F]/5 px-2.5 py-1 rounded-full"><PlayCircle className="w-3.5 h-3.5" /> On-demand recording</span>}
                      {w.free && <span className="text-xs font-semibold text-[#1A3A5F] bg-[#1A3A5F]/5 px-2.5 py-1 rounded-full">Free</span>}
                    </div>
                    <h2 className="font-heading text-xl font-bold text-gray-900 mt-3 leading-snug">{w.title}</h2>
                    {w.headline && <p className="text-sm font-semibold text-[#00857D] mt-1.5">{w.headline}</p>}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-4 text-sm font-semibold text-[#1A3A5F]">
                      <span className="inline-flex items-center gap-1.5">{w.on_demand ? <PlayCircle className="w-4 h-4 text-[#00A79D]" /> : <Clock className="w-4 h-4 text-[#00A79D]" />} {w.on_demand ? 'Watch on demand - anytime' : w.when_label}</span>
                      {!w.on_demand && w.platform && <span className="inline-flex items-center gap-1.5 text-gray-500 font-medium"><Video className="w-4 h-4" /> Online via {w.platform}</span>}
                    </div>
                    {w.no_suppliers && <p className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1 mt-3 inline-block">No suppliers on the call - no pitch.</p>}
                  </div>
                  <div className="p-6 flex-1 space-y-5">
                    <p className="text-sm text-gray-600 leading-relaxed">{w.summary}</p>
                    {(w.takeaways || []).length > 0 && (
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-2">What you'll take away</p>
                        <ul className="space-y-1.5">
                          {w.takeaways.map((t, i) => (
                            <li key={i} className="flex gap-2 text-sm text-gray-700"><Check className="w-4 h-4 text-[#00A79D] shrink-0 mt-0.5" /> {t}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {(w.agenda || []).length > 0 && (
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-2">On the agenda</p>
                        <div className="flex flex-wrap gap-1.5">
                          {w.agenda.map((a, i) => <span key={i} className="text-xs text-gray-600 bg-gray-50 border border-gray-200 px-2.5 py-1 rounded-full">{a}</span>)}
                        </div>
                      </div>
                    )}
                    {w.who_for && <p className="text-xs text-gray-400"><span className="font-semibold text-gray-500">Who it's for:</span> {w.who_for}</p>}
                    <div className="pt-2 border-t border-dashed border-gray-100"><SignupForm slug={w.slug} onDemand={w.on_demand} /></div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <p className="text-center text-sm text-gray-500 mt-8">
          Sign up once and watch the full recording instantly - we'll also email you the slides.
        </p>
      </section>

      {/* CTA */}
      <section className="bg-[#F9FAFB] border-t border-[#E5E7EB]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <h2 className="font-heading text-2xl font-bold text-gray-900">Prefer to explore on your own?</h2>
          <p className="text-gray-600 mt-2">Browse the full directory of care technology suppliers any time.</p>
          <a href="/browse" className="inline-flex items-center gap-1.5 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-6 py-3 rounded-md mt-5 transition-colors" data-testid="webinars-browse-cta">
            Browse the directory <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>
    </div>
  );
};

export default Webinars;
